/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { Field } from 'react-final-form';
import { Dropdown, Text, isEmptyValue } from '@blend/enterprise-components';

import { WrappedInput } from 'blb-components/WrappedInput';
import { respondToAuthChallenge } from 'blb-api/auth';
import { FormCard } from 'blb-components/FormCard';
import { FinalFormInputConfig } from 'blb-ui-types/helpers';
import { SetUser } from 'blb-ui-types/login';
import { principalTypeDisplayName, useLoginFlowSubmit, useChallengeCookie } from 'blb-utils/login';
import { BlendBridge } from 'blb-types/blendBridge';

export namespace SelectPrincipalPage {
  export interface Props {
    setMe: SetUser;
  }
}

export const SelectPrincipalPage: React.FC<SelectPrincipalPage.Props> = ({ setMe }) => {
  const onSubmit = useLoginFlowSubmit<BlendBridge.API.SelectPrincipalForm, BlendBridge.API.SelectPrincipalChallenge>(
    respondToAuthChallenge,
    setMe,
  );

  const [principals, setPrincipals] = useState<BlendBridge.Principal[]>([]);
  const challengeCookieResult = useChallengeCookie();
  useEffect(() => {
    if (challengeCookieResult.type === 'failure' || challengeCookieResult.cookie.challengeName !== 'SELECT_PRINCIPAL') {
      return;
    }
    const { principals } = challengeCookieResult.cookie.meta;
    setPrincipals(principals);
  }, []);

  return (
    <FormCard<BlendBridge.API.SelectPrincipalForm>
      headerLabel={<Text>Please select the role you'd like to use.</Text>}
      headerTitle="Select role"
      id="SelectPrincipal"
      onSubmit={onSubmit}
      submitLabel="Confirm"
    >
      <Field
        {...({
          autoFocus: true,
          component: WrappedInput,
          customComponentProps: {
            options: principals?.map(principal => ({
              text: principalTypeDisplayName[principal.type] || principal.type,
              value: principal.id,
            })),
          },
          inputComponent: Dropdown,
          label: 'Role',
          labelSize: 'sm',
          name: 'principalId',
          validate: isEmptyValue('Please select a role.'),
        } as FinalFormInputConfig<Dropdown.Props, string>)}
      />
    </FormCard>
  );
};
